import { CancelOutlined, CheckCircleOutline } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { deleteProAlert } from '../../../../../api/pro-alert';
import SimpleDialog from '../../../../../common-components/dialog/SimpleDialog';
import { useAppDispatch } from '../../../../../hooks';
import useUserRoleAndShortId from '../../../../../hooks/user-details/useUserDetails';
import {
  ProAlertDeleteResponse,
  ProAlertDialogStatus,
} from '../../../../../models/pro.model';
import { removeProAlert } from '../../../../../state/pro-alerts/pro-alerts.slice';
import {
  getCountAnalyticsEventLabel,
  getUserAnalyticsEventLabel,
  logAnalyticsEvent,
} from '../../../../../utils/analytics';

interface ProAlertDeleteDialogProps {
  open: boolean;
  alert_id: string | null;
  resetState: () => void;
}

function ProAlertDeleteDialog({
  open,
  alert_id,
  resetState,
}: ProAlertDeleteDialogProps) {
  const dispatch = useAppDispatch();
  const CONTENT_MIN_HEIGHT = 80;
  const CONTENT_MAX_HEIGHT = 400;
  const CONTENT_MAX_WIDTH = 500;
  const [status, setStatus] = useState<ProAlertDialogStatus>(
    ProAlertDialogStatus.Idle
  );
  const [deleteAlertResponse, setDeleteAlertResponse] =
    useState<ProAlertDeleteResponse | null>(null);
  const userDetails = useUserRoleAndShortId();

  if (!alert_id) return null;

  const handleDeleteConfirmed = async () => {
    setStatus(ProAlertDialogStatus.Loading);
    setDeleteAlertResponse(null);

    try {
      const response = await deleteProAlert({ alert_id });
      setStatus(ProAlertDialogStatus.Success);
      setDeleteAlertResponse(response);
      dispatch(removeProAlert(alert_id));
      logAnalyticsEvent('MyFleet', 'ProAlertsDeleted', [
        getUserAnalyticsEventLabel(userDetails),
        getCountAnalyticsEventLabel(1),
      ]);
    } catch (error: any) {
      setStatus(ProAlertDialogStatus.Error);
    }
  };

  const handleDialogClose = () => {
    setDeleteAlertResponse(null);
    setStatus(ProAlertDialogStatus.Idle);
    resetState();
  };

  const handleCancel = () => {
    resetState();
    setDeleteAlertResponse(null);
    setStatus(ProAlertDialogStatus.Idle);
  };

  const renderTitle = () => {
    switch (status) {
      case ProAlertDialogStatus.Error:
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <CancelOutlined sx={{ color: 'error.main' }} />

            <Typography>
              An error occurred during PRO alert deletion.
            </Typography>
          </Box>
        );
      case ProAlertDialogStatus.Success:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleOutline sx={{ color: 'secondary.main' }} />
            <Typography data-testid="delete-pro-alert-success-dialog-content">
              {deleteAlertResponse?.message as string}
            </Typography>
          </Box>
        );
      case ProAlertDialogStatus.Loading:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} sx={{ color: 'secondary.main' }} />
            <Typography data-testid="delete-pro-alert-dialog-loading">
              Deleting Alert...
            </Typography>
          </Box>
        );
      case ProAlertDialogStatus.Idle:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography data-testid="delete-pro-alert-dialog-content">
              Are you sure you wish to delete this Alert?
            </Typography>
          </Box>
        );
      default:
        return <Typography>Delete PRO Alert</Typography>;
    }
  };

  const renderActions = () => (
    <Stack
      direction="column"
      spacing={1}
      justifyContent={
        status === ProAlertDialogStatus.Loading ? 'flex-end' : 'space-between'
      }
      sx={{ width: '100%' }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        {status === 'error' && (
          <Typography sx={{ alignItems: 'flex-start', width: '100%' }}>
            This PRO alert has not been deleted.
          </Typography>
        )}
        <Button
          variant={
            status !== ProAlertDialogStatus.Idle ? undefined : 'outlined'
          }
          onClick={
            status === ProAlertDialogStatus.Success ||
            status === ProAlertDialogStatus.Error
              ? handleDialogClose
              : handleCancel
          }
          data-testid="delete-pro-alert-dialog-close-button"
          disabled={status === ProAlertDialogStatus.Loading}
          sx={{ mb: 0, minWidth: '80px' }}
        >
          {status !== ProAlertDialogStatus.Idle ? 'Close' : 'Cancel'}
        </Button>
        <Button
          onClick={handleDeleteConfirmed}
          disabled={
            status === ProAlertDialogStatus.Loading ||
            status === ProAlertDialogStatus.Success ||
            status === ProAlertDialogStatus.Error
          }
          data-testid="delete-pro-alert-deletion-confirm-button"
          variant="contained"
          color="error"
        >
          Confirm
        </Button>
      </Box>
    </Stack>
  );

  const renderContent = () => (
    <Box
      sx={{
        minHeight: CONTENT_MIN_HEIGHT,
        maxHeight: CONTENT_MAX_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexGrow: 1,
          mt: 1,
          mb: 1,
        }}
      >
        {renderTitle()}
      </Box>
    </Box>
  );

  return (
    <SimpleDialog
      onClose={handleDialogClose}
      open={open}
      title="Delete PRO Alert"
      width={CONTENT_MAX_WIDTH}
      actions={renderActions()}
      data-testid="delete-pro-alert-dialog"
    >
      {renderContent()}
    </SimpleDialog>
  );
}

export default ProAlertDeleteDialog;
